import React, { useState, useEffect, useCallback } from 'react'
import { Table, Button, Input, Modal, Form, Tag, Space, Select, Alert, Tooltip, DatePicker } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import { apiRequest } from '../../helpers'
import EmailSourceButton from './EmailSourceButton'
import dayjs from 'dayjs'

const apiUrl = process.env.REACT_APP_API
const MONEDE = ['RON', 'EUR', 'USD']
const monedaColor = { RON: 'green', EUR: 'blue', USD: 'cyan' }

const formatPret = (pret, moneda) => {
  if (pret == null) return <span style={{ color: '#aaa' }}>—</span>
  return (
    <>
      <strong>{Number(pret).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</strong>{' '}
      <Tag color={monedaColor[moneda] || 'default'} style={{ fontSize: 10, marginLeft: 4 }}>{moneda}</Tag>
    </>
  )
}

const TpmRfq = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [filterMoneda, setFilterMoneda] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [meta, setMeta] = useState({ totalItems: 0 })
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const fetchItems = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize), sortBy: 'createdAt:DESC', ...(search ? { search } : {}), ...(filterMoneda ? { 'filter.moneda': filterMoneda } : {}) })
      const res = await apiRequest(`${apiUrl}tpm/rfq-items?${params}`, 'GET')
      if (res?.data?.data) { setItems(res.data.data); setMeta({ totalItems: res.data.meta?.totalItems || 0 }) }
    } catch { setError('Eroare la incarcarea cererilor de oferta.') }
    finally { setLoading(false) }
  }, [page, pageSize, search, filterMoneda])

  useEffect(() => { fetchItems() }, [fetchItems])

  const openCreate = () => { setSelectedItem(null); form.resetFields(); setModalOpen(true) }
  const openEdit = (item) => {
    setSelectedItem(item)
    form.setFieldsValue({ partNumber: item.partNumber, ean: item.ean || '', denumire: item.denumire || '', firma: item.firma || '', email: item.email || '', pret: item.pret ?? '', moneda: item.moneda || '', cantitate: item.cantitate ?? '', observatii: item.observatii || '', dataOferta: item.dataOferta ? dayjs(item.dataOferta) : null })
    setModalOpen(true)
  }

  const handleSave = async () => {
    const values = await form.validateFields(); setSaving(true)
    const payload = { ...values, pret: values.pret !== '' && values.pret != null ? Number(values.pret) : null, cantitate: values.cantitate !== '' && values.cantitate != null ? Number(values.cantitate) : null, dataOferta: values.dataOferta ? values.dataOferta.format('YYYY-MM-DD') : null }
    try {
      if (selectedItem) await apiRequest(`${apiUrl}tpm/rfq-items/${selectedItem.id}`, 'PATCH', payload)
      else await apiRequest(`${apiUrl}tpm/rfq-items`, 'POST', payload)
      setModalOpen(false); fetchItems()
    } catch (e) { Modal.error({ title: 'Eroare', content: e?.response?.data?.message || 'Eroare la salvare.' }) }
    finally { setSaving(false) }
  }

  const handleDelete = async () => {
    try { await apiRequest(`${apiUrl}tpm/rfq-items/${selectedItem.id}`, 'DELETE'); setDeleteModalOpen(false); fetchItems() }
    catch { Modal.error({ title: 'Eroare', content: 'Eroare la stergere.' }) }
  }

  const columns = [
    { title: 'Part Number', dataIndex: 'partNumber', key: 'partNumber', render: (v) => <span>{v}</span> },
    { title: 'Denumire', key: 'denumire', render: (_, r) => r.denumire || r.part?.denumire || <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Firma', key: 'firma', render: (_, r) => (<div><div>{r.firma || <span style={{ color: '#aaa' }}>—</span>}</div>{r.email && <small style={{ color: '#888' }}>{r.email}</small>}</div>) },
    { title: 'Pret', key: 'pret', render: (_, r) => formatPret(r.pret, r.moneda) },
    { title: 'Pret EUR', key: 'pretEur', render: (_, r) => r.pretEur != null ? <><strong>{Number(r.pretEur).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</strong> <Tag color="blue">EUR</Tag></> : <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Cantitate', dataIndex: 'cantitate', key: 'cantitate', render: (v) => v ?? <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Data RFQ', dataIndex: 'dataOferta', key: 'dataOferta', render: (v) => v ? new Date(v).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Email sursa', key: 'emailMessageId', render: (_, r) => <EmailSourceButton emailMessageId={r.emailMessageId} /> },
    { title: 'Actiuni', key: 'actions', width: 100, render: (_, r) => (<Space><Tooltip title="Editeaza"><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip><Tooltip title="Sterge"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => { setSelectedItem(r); setDeleteModalOpen(true) }} /></Tooltip></Space>) },
  ]

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <strong style={{ fontSize: 16 }}>Cereri de Oferta (RFQ)</strong>
          <Space wrap>
            <Input.Search placeholder="Cauta part, firma, denumire..." value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} style={{ width: 260 }} allowClear />
            <Select style={{ width: 110 }} value={filterMoneda || undefined} onChange={v => { setFilterMoneda(v || ''); setPage(1) }} allowClear placeholder="Moneda">
              {MONEDE.map(m => <Select.Option key={m} value={m}>{m}</Select.Option>)}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Adauga cerere</Button>
            <Button icon={<ReloadOutlined />} onClick={fetchItems} />
          </Space>
        </div>
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 12 }} />}
        <Table columns={columns} dataSource={items} rowKey="id" loading={loading} size="small" scroll={{ y: 420 }}
          pagination={{ current: page, pageSize, total: meta.totalItems, onChange: (p, ps) => { setPage(p); setPageSize(ps) }, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100], showTotal: (total) => `Total: ${total} cereri` }}
          locale={{ emptyText: 'Nu exista cereri de oferta.' }} />
      </div>

      <Modal title={selectedItem ? 'Editeaza cerere' : 'Adauga cerere noua'} open={modalOpen} onOk={handleSave} onCancel={() => setModalOpen(false)} confirmLoading={saving} okText={saving ? 'Se salveaza...' : 'Salveaza'} cancelText="Anuleaza" width={700}>
        <Form form={form} layout="vertical">
          <div className="row g-3">
            <div className="col-md-4"><Form.Item label="Part Number" name="partNumber" rules={[{ required: true, message: 'Part Number este obligatoriu' }]}><Input disabled={!!selectedItem} placeholder="ex: ABC123" /></Form.Item></div>
            <div className="col-md-4"><Form.Item label="EAN" name="ean"><Input placeholder="ex: 1234567890123" /></Form.Item></div>
            <div className="col-md-4"><Form.Item label="Denumire" name="denumire"><Input placeholder="Denumirea produsului" /></Form.Item></div>
            <div className="col-md-4"><Form.Item label="Firma" name="firma"><Input placeholder="Numele firmei" /></Form.Item></div>
            <div className="col-md-4"><Form.Item label="Email contact" name="email"><Input placeholder="contact@firma.ro" /></Form.Item></div>
            <div className="col-md-3"><Form.Item label="Pret target" name="pret"><Input type="number" step="0.01" placeholder="0.00" /></Form.Item></div>
            <div className="col-md-2"><Form.Item label="Moneda" name="moneda"><Select allowClear placeholder="—">{MONEDE.map(m => <Select.Option key={m} value={m}>{m}</Select.Option>)}</Select></Form.Item></div>
            <div className="col-md-3"><Form.Item label="Cantitate" name="cantitate"><Input type="number" placeholder="buc" /></Form.Item></div>
            <div className="col-md-4"><Form.Item label="Data RFQ" name="dataOferta"><DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" /></Form.Item></div>
            <div className="col-md-8"><Form.Item label="Observatii" name="observatii"><Input placeholder="Observatii suplimentare" /></Form.Item></div>
          </div>
        </Form>
      </Modal>

      <Modal title="Confirmare stergere" open={deleteModalOpen} onOk={handleDelete} onCancel={() => setDeleteModalOpen(false)} okText="Sterge" okButtonProps={{ danger: true }} cancelText="Anuleaza">
        <p>Stergi cererea pentru <strong>{selectedItem?.partNumber}</strong>?</p>
      </Modal>
    </div>
  )
}

export default TpmRfq
